import { Stack, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'src/components/hook-form/form-provider';
import {
  createCodingPacket,
  createPrescreeningQues,
  editJobDetails,
  updateCodingPacket,
} from 'src/services/role.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useSnackbar } from 'notistack';
import { formatDate } from 'src/utils/formatDate';
import {
  editInterviewQuestions,
  enableTwoWayInterview,
} from 'src/services/interview.service';
import { useJobCreationContext } from '../../hooks/use-job-creation-context';
import PreScreening from './sections/pre-screening';
import AIInterview from './sections/ai-interview';
import Screening from './sections/screening';

export default function AutopilotWorkflow() {
  const {
    canEdit,
    mode,
    JOB_CREATION_STATES,
    setCurrentState,
    jobId,
    jobSkills,
    jobPrimarySkill: savedJobPrimarySkill,
    setJobPrimarySkill,
    jobDetails: savedJobDetails,
    preScreeningQuestions: savedPreScreeningQuestions,
    setPreScreeningQuestions,
    preferredQualifyingCriterias: savedPreferredQualifyingCriterias,
    setPreferredQualifyingCriterias,
    interviewQuestions: savedInterviewQuestions,
    setInterviewQuestions,
    interviewDeadline: savedInterviewDeadline,
    codingAssessments: savedCodingAssessments,
    setCodingAssessments,
    interactiveInterview: savedInteractiveInterview,
    setInteractiveInterview,
    setInterviewDeadline,
    setErrors: setValidationErrors,
    setRevalidateFunction,
    clickedStage,
  } = useJobCreationContext();

  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);
  const [canFocus, setCanFocus] = useState(true);

  const onError = () => {
    setCanFocus(true);
  };

  const { enqueueSnackbar } = useSnackbar();

  // variables
  const defaultValues = useMemo(
    () => ({
      preScreeningQuestions: savedPreScreeningQuestions,
      preferredQualifyingCriterias: savedPreferredQualifyingCriterias,
      noOfQuestions: '',
      jobPrimarySkill: savedJobPrimarySkill,
      interviewQuestions: savedInterviewQuestions,
      aiInterviewDeadline: savedInterviewDeadline,
      codingAssessments: savedCodingAssessments,
      interactiveInterview: savedInteractiveInterview,
    }),
    [
      savedPreScreeningQuestions,
      savedPreferredQualifyingCriterias,
      savedJobPrimarySkill,
      savedInterviewQuestions,
      savedInterviewDeadline,
      savedCodingAssessments,
      savedInteractiveInterview,
    ],
  );

  const schema = Yup.object().shape({
    preScreeningQuestions: Yup.array().of(
      Yup.object().shape({
        id: Yup.string(),
        title: Yup.string(),
        question: Yup.string().required('Question is required'),
        responseType: Yup.string().required('Response Type is required'),
        min: Yup.number().when(['responseType'], {
          is: val => val === 'NUMERIC',
          then: schema =>
            schema
              .transform((value, originalValue) => {
                if (originalValue === '') return undefined;
                return value;
              })
              .required('Min value is required')
              .integer('Decimal values are not allowed')
              .typeError('It should be a valid number')
              .min(0, 'Min value must be greater than 0')
              .test(
                'is-min-less-than-max',
                'Min must be less than Max',
                function (value) {
                  const { max } = this.parent;
                  if (max === undefined) return true;
                  return parseFloat(value) < parseFloat(max);
                },
              ),
        }),
        max: Yup.number().when(['responseType'], {
          is: val => val === 'NUMERIC',
          then: schema =>
            schema
              .transform((value, originalValue) => {
                if (originalValue === '') return undefined;
                return value;
              })
              .required('Max value is required')
              .integer('Decimal values are not allowed')
              .typeError('It should be a valid number')
              .min(0, 'Max value must be greater than 0')
              .test(
                'is-max-more-than-min',
                'Max must be more than Min',
                function (value) {
                  const { min } = this.parent;
                  if (min === undefined) return true;
                  return parseFloat(value) > parseFloat(min);
                },
              ),
        }),
        booleanAnswer: Yup.string(),
        isQualifyingQuestion: Yup.bool(),
      }),
    ),
    preferredQualifyingCriterias: Yup.array().of(
      Yup.object().shape({
        title: Yup.string(),
        criteria: Yup.string().test(
          'is-criteria-valid',
          'Criteria is required',
          function validateCriteria(value) {
            const { title } = this.parent;
            if (!value) {
              return this.createError({ message: 'Criteria is required' });
            }
            return true;
          },
        ),
      }),
    ),
    jobPrimarySkill: Yup.string().required('Primary Skill is required'),
    noOfQuestions: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === '') return undefined;
        return value;
      })
      .typeError('Please enter a number (0-9)'),
    interviewQuestions: Yup.array().of(
      Yup.object().shape({
        question: Yup.string().required('Question cannot be blank'),
      }),
    ),
    aiInterviewDeadline: Yup.string(),
    codingAssessments: Yup.array().of(
      Yup.object().shape({
        id: Yup.string(),
        questions: Yup.array().of(
          Yup.object().shape({
            text: Yup.string().required('Question is required'),
            testCases: Yup.array()
              .of(
                Yup.object().shape({
                  input: Yup.string().required('Input is required'),
                  output: Yup.string().required('Output is required'),
                }),
              )
              .min(1, 'At least one test case is required'),
            timeLimit: Yup.number().required('Time limit is required'),
          }),
        ),
        languages: Yup.array()
          .of(Yup.string().required('Language is required'))
          .min(1, 'At least one language is required'),
      }),
    ),
    interactiveInterview: Yup.object().shape({
      showInteractiveInterview: Yup.bool(),
      criteriaText: Yup.string().when(['showInteractiveInterview'], {
        is: true,
        then: schema => schema.required('Criteria is required'),
      }),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    shouldFocusError: false,
  });

  const {
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const watchPreScreeningQuestions = useWatch({
    control,
    name: 'preScreeningQuestions',
  });

  const watchPreferredQualifyingCriterias = useWatch({
    control,
    name: 'preferredQualifyingCriterias',
  });

  const watchNoOfQuestions = useWatch({
    control,
    name: 'noOfQuestions',
  });

  const watchInterviewQuestions = useWatch({
    control,
    name: 'interviewQuestions',
  });

  const watchAiInterviewDeadline = useWatch({
    control,
    name: 'aiInterviewDeadline',
  });

  const watchCodingAssessments = useWatch({
    control,
    name: 'codingAssessments',
  });

  const watchJobPrimarySkill = useWatch({
    control,
    name: 'jobPrimarySkill',
  });

  const {
    fields: preScreeningQuestionFields,
    append: appendPreScreeningQuestion,
    remove: removePreScreeningQuestion,
  } = useFieldArray({
    control,
    name: 'preScreeningQuestions',
  });

  const {
    fields: preferredQualifyingCriteriaFields,
    append: appendPreferredQualifyingCriteria,
    remove: removePreferredQualifyingCriteria,
  } = useFieldArray({
    control,
    name: 'preferredQualifyingCriterias',
  });

  const {
    fields: interviewQuestionFields,
    append: appendInterviewQuestion,
    remove: removeInterviewQuestion,
  } = useFieldArray({
    control,
    name: 'interviewQuestions',
  });

  const {
    fields: codingAssessmentFields,
    append: appendCodingAssessment,
    remove: removeCodingAssessment,
  } = useFieldArray({
    control,
    name: 'codingAssessments',
  });

  const watchInteractiveInterview = useWatch({
    control,
    name: 'interactiveInterview',
  });

  // const addQuestion = handleSubmit(async data => {
  //   await trigger();

  //   const newQues = {
  //     text: data.question,
  //     response_type: data.responseType,
  //   };

  //   if (data.responseType === 'BOOLEAN') {
  //     newQues.response_parameters = {
  //       IDEAL_ANSWER: data.booleanAnswer,
  //     };
  //   }
  //   if (data.responseType === 'NUMERIC') {
  //     newQues.response_parameters = {
  //       MIN_VALUE: data.min,
  //       MAX_VALUE: data.max,
  //     };
  //   }
  //   if (data.responseType === 'TEXT') {
  //     newQues.response_parameters = {
  //       IDEAL_ANSWER: '-',
  //     };
  //   }

  //   addPreScreeningQuestion(newQues);
  //   setValidationSchema(initialSchema);
  //   reset();
  // });

  // const updateValidationSchema = newValue => {
  //   let updatedSchema;
  //   let newObj = null;
  //   if (newValue === 'NUMERIC') {
  //     updatedSchema = Yup.object().shape({
  //       preScreeningQuestions: Yup.array().of(
  //         Yup.object().shape({
  //           question: Yup.string().required('Question is required'),
  //           responseType: Yup.string().required('Response Type is required'),
  //           min: Yup.number().required('Min value is required'),
  //           max: Yup.number()
  //             .required('Max value is required')
  //             .moreThan(
  //               Yup.ref('min'),
  //               'Max value must be greater than Min value',
  //             ),
  //           isQualifyingQuestion: Yup.bool(),
  //         }),
  //       ),
  //     });
  //     newObj = {
  //       question: oldQuestion?.text || '',
  //       responseType: oldQuestion?.response_type || '',
  //       min: oldQuestion?.response_parameters?.MIN_VALUE || 0,
  //       max: oldQuestion?.response_parameters?.MAX_VALUE || 0,
  //     };
  //     // if (!oldQuestion) setDefaultValues(newObj);
  //   } else if (newValue === 'BOOLEAN') {
  //     updatedSchema = Yup.object().shape({
  //       preScreeningQuestions: Yup.array().of(
  //         Yup.object().shape({
  //           question: Yup.string().required('Question is required'),
  //           responseType: Yup.string().required('Response Type is required'),
  //           booleanAnswer: Yup.string().required('Ideal Answer is required'),
  //           isQualifyingQuestion: Yup.bool(),
  //         }),
  //       ),
  //     });
  //     newObj = {
  //       question: oldQuestion?.text || '',
  //       responseType: oldQuestion?.response_type || '',
  //       booleanAnswer: oldQuestion?.response_parameters?.IDEAL_ANSWER || '',
  //     };
  //     // if (!oldQuestion) setDefaultValues(newObj)
  //   } else if (newValue === 'TEXT') {
  //     updatedSchema = Yup.object().shape({
  //       preScreeningQuestions: Yup.array().of(
  //         Yup.object().shape({
  //           question: Yup.string().required('Question is required'),
  //           responseType: Yup.string().required('Response Type is required'),
  //           isQualifyingQuestion: Yup.bool(),
  //         }),
  //       ),
  //     });
  //     newObj = {
  //       question: oldQuestion?.text || '',
  //       responseType: oldQuestion?.response_type || '',
  //     };
  //     // if (!oldQuestion) setDefaultValues(newObj);
  //   } else updatedSchema = initialSchema;
  //   setValidationSchema(updatedSchema);

  //   if (oldQuestion && newObj) {
  //     Object.keys(newObj).forEach(fieldName => {
  //       setValue(fieldName, newObj[fieldName]);
  //     });
  //   }
  //   setOldQuestion(null);
  // };

  // const handleResponseTypeChange = event => {
  //   const newValue = event.target.value;
  //   setValue('responseType', newValue);
  //   updateValidationSchema(newValue);
  // };

  // functions

  // states

  const [isPreScreeningExpanded, setIsPreScreeningExpanded] = useState(false);
  const [isScreeningExpanded, setIsScreeningExpanded] = useState(false);
  const [isAiInterviewExpanded, setIsAiInterviewExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const savePreScreeningQuestions = async () => {
    const preScreeningQuestions = getValues('preScreeningQuestions');

    const payloadPreScreeningQuestions = preScreeningQuestions.map(item => {
      const newQues = {
        title: item.title,
        text: item.question,
        response_type: item.responseType,
        is_qualifying_question: item.isQualifyingQuestion,
      };

      if (item.id) {
        newQues.question_id = item.id;
      }

      if (item.responseType === 'BOOLEAN') {
        newQues.response_parameters = {
          IDEAL_ANSWER: item.booleanAnswer,
        };
      }

      if (item.responseType === 'NUMERIC') {
        newQues.response_parameters = {
          MIN_VALUE: item.min,
          MAX_VALUE: item.max,
        };
      }

      if (item.responseType === 'TEXT') {
        newQues.response_parameters = {
          IDEAL_ANSWER: '-',
        };
      }

      return newQues;
    });

    const preScreeningQuestionsResponse = await createPrescreeningQues(
      { questions: payloadPreScreeningQuestions },
      organization_id,
      jobId,
    );

    return preScreeningQuestionsResponse;
  };

  const savePreferredQualifyingCriterias = async () => {
    const preferredQualifyingCriterias = getValues(
      'preferredQualifyingCriterias',
    );

    const preferrredQualifyingResponse = await editJobDetails(
      {
        criteria_list: preferredQualifyingCriterias.map(item => item.criteria),
      },
      organization_id,
      jobId,
    );

    return preferrredQualifyingResponse;
  };

  const savePrimarySkill = async () => {
    const payload = {
      skills: jobSkills,
    };

    const primarySkillResponse = await editJobDetails(
      payload,
      organization_id,
      jobId,
    );

    return primarySkillResponse;
  };

  const saveInterviewQuestions = async () => {
    const interviewQuestions = getValues('interviewQuestions');

    const interviewQuestionsRequest = interviewQuestions.map(
      (question, index) => {
        const existingQuestion = savedJobDetails.questions[index];

        if (!existingQuestion) {
          return {
            text: question.question,
            skills: [],
            sl_no: index + 1,
          };
        }

        const newQuestion = {
          ...existingQuestion,
          text: question.question,
          updated_at: formatDate(new Date()),
        };

        return newQuestion;
      },
    );

    const interviewQuestionsResponse = await editInterviewQuestions(
      organization_id,
      jobId,
      {
        questions: interviewQuestionsRequest,
      },
    );

    return interviewQuestionsResponse;
  };

  const saveAiInterviewDeadline = async () => {
    const aiInterviewDeadline = getValues('aiInterviewDeadline');
    const aiInterviewDeadlineResponse = await editJobDetails(
      {
        ai_interview_validity: aiInterviewDeadline,
      },
      organization_id,
      jobId,
    );
    return aiInterviewDeadlineResponse;
  };

  const saveCodingAssessment = async () => {
    const codingAssessments = getValues('codingAssessments');

    const codingAssessmentPromises = codingAssessments.map(
      async codingAssessment => {
        const packetId = codingAssessment.id;
        const codingQuestions = codingAssessment.questions;
        const codingLanguages = codingAssessment.languages;

        const payload = {
          interview_name: 'Coding Assessment',
          coding_questions: codingQuestions.map((question, index) => {
            const newQuestion = {
              question_text: question.text,
              input_output_list: question.testCases.map(testCase => ({
                input: testCase.input,
                output: testCase.output,
              })),
              time_limit_minutes: question.timeLimit,
            };

            return newQuestion;
          }),
          allowed_coding_languages: codingLanguages,
        };

        if (packetId) {
          return updateCodingPacket(organization_id, packetId, payload);
        }

        return createCodingPacket(organization_id, jobId, payload);
      },
    );

    const codingAssessmentResponses = await Promise.all(
      codingAssessmentPromises,
    );

    const codingAssessmentResponse = codingAssessmentResponses.reduce(
      (acc, response) => {
        if (response.success) {
          acc.success = true;
        }
        return acc;
      },
    );

    return codingAssessmentResponse;
  };

  const saveInteractiveInterview = async () => {
    const criteriaText = getValues('interactiveInterview.criteriaText');

    const payload = {
      interview_requirements: criteriaText,
    };

    const interactiveInterviewResponse = await enableTwoWayInterview(
      organization_id,
      jobId,
      payload,
    );

    return interactiveInterviewResponse;
  };

  const handleAutoPilotWorkow = handleSubmit(async data => {
    if (
      (mode === 'CREATE' ? !data.noOfQuestions : true) &&
      !data.interviewQuestions.length
    ) {
      setError('noOfQuestions', {
        message: 'Please enter numeric value',
      });
      enqueueSnackbar({
        message: 'Please generate interview questions',
        variant: 'error',
      });
    } else if (!data.interviewQuestions.length) {
      setError('interviewQuestions', {
        message: 'Please generate interview questions',
      });
      enqueueSnackbar({
        message: 'Please generate interview questions',
        variant: 'error',
      });
    } else if (!data.aiInterviewDeadline) {
      setError('aiInterviewDeadline', {
        message: 'Please enter AI interview deadline',
      });
      enqueueSnackbar({
        message: 'Please enter AI interview deadline',
        variant: 'error',
      });
    } else {
      try {
        setIsSubmitting(true);

        const responses = {
          preScreeningQuestions: {
            success: null,
          },
          preferredCriteria: {
            success: null,
          },
          primarySkillResponse: {
            success: null,
          },
          interviewQuestionsResponse: {
            success: null,
          },
          interviewDeadlineResponse: {
            success: null,
          },
          codingAssessmentResponse: {
            success: null,
          },
          interactiveInterview: {
            success: null,
          },
        };

        const codingAssessments = getValues('codingAssessments');
        const showInteractiveInterview = getValues(
          'interactiveInterview.showInteractiveInterview',
        );

        if (canEdit) {
          responses.preScreeningQuestions = await savePreScreeningQuestions();
          responses.preferredCriteria =
            await savePreferredQualifyingCriterias();
          responses.primarySkillResponse = await savePrimarySkill();
          responses.interviewQuestionsResponse = await saveInterviewQuestions();
          responses.interviewDeadlineResponse = await saveAiInterviewDeadline();
          if (codingAssessments.length) {
            responses.codingAssessmentResponse = await saveCodingAssessment();
          }
          if (showInteractiveInterview) {
            responses.interactiveInterview = await saveInteractiveInterview();
          }
        }

        if (
          responses.preScreeningQuestions.success !== null &&
          responses.preferredCriteria.success !== null &&
          responses.primarySkillResponse.success !== null &&
          responses.interviewQuestionsResponse.success !== null &&
          responses.interviewDeadlineResponse.success !== null &&
          (!(codingAssessments.length > 0) ||
            responses.codingAssessmentResponse.success !== null) &&
          (!showInteractiveInterview ||
            responses.interactiveInterview.success !== null)
        ) {
          if (
            responses.preferredCriteria.success &&
            responses.preScreeningQuestions.success &&
            responses.primarySkillResponse.success &&
            responses.interviewQuestionsResponse.success &&
            responses.interviewDeadlineResponse.success &&
            (!(codingAssessments.length > 0) ||
              responses.codingAssessmentResponse.success) &&
            (!showInteractiveInterview ||
              responses.interactiveInterview.success !== null)
          ) {
            enqueueSnackbar('Autopilot workflow saved successfully');
            setCurrentState(JOB_CREATION_STATES.LAUNCH.value);
          } else throw new Error('Error while saving Autopilot workflow');
        } else {
          enqueueSnackbar('Autopilot workflow saved successfully');
          setCurrentState(JOB_CREATION_STATES.LAUNCH.value);
        }
      } catch (error) {
        console.log(error);
        enqueueSnackbar('Error while saving Autopilot workflow');
      } finally {
        setIsSubmitting(false);
      }
    }
  }, onError);

  // effects
  useEffect(() => {
    setPreScreeningQuestions(watchPreScreeningQuestions);
    setPreferredQualifyingCriterias(watchPreferredQualifyingCriterias);
    setInterviewQuestions(watchInterviewQuestions);
    setInterviewDeadline(watchAiInterviewDeadline);
    setCodingAssessments(watchCodingAssessments);
    setJobPrimarySkill(watchJobPrimarySkill);
    setInteractiveInterview(watchInteractiveInterview);
  }, [
    setCodingAssessments,
    setInteractiveInterview,
    setInterviewDeadline,
    setInterviewQuestions,
    setJobPrimarySkill,
    setPreScreeningQuestions,
    setPreferredQualifyingCriterias,
    watchAiInterviewDeadline,
    watchCodingAssessments,
    watchInteractiveInterview,
    watchInterviewQuestions,
    watchJobPrimarySkill,
    watchPreScreeningQuestions,
    watchPreferredQualifyingCriterias,
  ]);

  const checkAiInterview = useCallback(() => {
    if (!watchInterviewQuestions.length && !watchNoOfQuestions) {
      setError('noOfQuestions', {
        message: 'Please enter numeric value',
      });
      enqueueSnackbar({
        message: 'Please generate interview questions',
        variant: 'error',
      });
      return false;
    }
    if (!watchInterviewQuestions.length) {
      setError('interviewQuestions', {
        message: 'Please generate interview questions',
      });
      enqueueSnackbar({
        message: 'Please generate interview questions',
        variant: 'error',
      });
      return false;
    }
    if (!watchAiInterviewDeadline) {
      setError('aiInterviewDeadline', {
        message: 'Please enter AI interview deadline',
      });
      enqueueSnackbar({
        message: 'Please enter AI interview deadline',
        variant: 'error',
      });
      return false;
    }
    return true;
  }, [
    enqueueSnackbar,
    setError,
    watchAiInterviewDeadline,
    watchInterviewQuestions.length,
    watchNoOfQuestions,
  ]);

  useEffect(() => {
    setRevalidateFunction(async () => {
      const result = checkAiInterview();

      if (result) return trigger();
      return false;
    });

    return () => {
      setRevalidateFunction(null);
    };
  }, [
    checkAiInterview,
    enqueueSnackbar,
    setError,
    setRevalidateFunction,
    trigger,
    watchInterviewQuestions.length,
    watchNoOfQuestions,
  ]);

  useEffect(() => {
    setValidationErrors(errors);
    if (errors) {
      onError();
    }

    return () => {
      setValidationErrors({});
    };
  }, [errors, setValidationErrors]);

  useEffect(() => {
    if (errors && canFocus) {
      if (errors?.preScreeningQuestions) {
        setIsPreScreeningExpanded(true);
      }
      if (errors?.preferredQualifyingCriterias || errors?.jobPrimarySkill) {
        setIsScreeningExpanded(true);
      }
      if (
        errors?.interviewQuestions ||
        errors?.aiInterviewDeadline ||
        errors?.noOfQuestions ||
        errors?.codingAssessments ||
        errors?.interactiveInterview
      ) {
        setIsAiInterviewExpanded(true);
      }

      // Sort inputs based on their position on the page. (the order will be based on validaton order otherwise)
      const preScreeningErrors = errors?.preScreeningQuestions || [];
      const preferredQualifyingCriteriasErrors =
        errors?.preferredQualifyingCriterias || [];
      const interviewQuestionsErrors = errors?.interviewQuestions || [];
      const aiInterviewDeadlineErrors = errors?.aiInterviewDeadline || {};
      const jobPrimarySkillErrors = errors?.jobPrimarySkill || {};
      const noOfQuestionsErrors = errors?.noOfQuestions || {};
      const codingAssessmentErrors = errors?.codingAssessments || [];
      const interactiveInterviewErrors = errors?.interactiveInterview || {};

      const elements = [];

      preScreeningErrors.forEach((item, errorIndex) => {
        const items = Object.keys(item);
        items.forEach(name => {
          const ele = document.getElementsByName(
            `preScreeningQuestions.${errorIndex}.${name}`,
          );
          ele.forEach(e => elements.push(e));
        });
      });

      preferredQualifyingCriteriasErrors.forEach((item, errorIndex) => {
        const items = Object.keys(item);
        items.forEach(name => {
          const ele = document.getElementsByName(
            `preferredQualifyingCriterias.${errorIndex}.${name}`,
          );
          ele.forEach(e => elements.push(e));
        });
      });

      Object.keys(aiInterviewDeadlineErrors).forEach(name => {
        const ele = document.getElementsByName(name);
        ele.forEach(e => elements.push(e));
      });

      Object.keys(jobPrimarySkillErrors).forEach(name => {
        const ele = document.getElementsByName(name);
        ele.forEach(e => elements.push(e));
      });

      Object.keys(noOfQuestionsErrors).forEach(name => {
        const ele = document.getElementsByName(name);
        ele.forEach(e => elements.push(e));
      });

      codingAssessmentErrors.forEach((item, errorIndex) => {
        const items = Object.keys(item);
        items.forEach(name => {
          const ele = document.getElementsByName(
            `codingAssessments.${errorIndex}.${name}`,
          );
          ele.forEach(e => elements.push(e));
        });
      });

      Object.keys(interactiveInterviewErrors).forEach(name => {
        const ele = document.getElementsByName(name);
        ele.forEach(e => elements.push(e));
      });

      if (Array.isArray(interviewQuestionsErrors)) {
        interviewQuestionsErrors.forEach((item, errorIndex) => {
          const items = Object.keys(item);
          items.forEach(name => {
            const ele = document.getElementsByName(
              `interviewQuestions.${errorIndex}.${name}`,
            );
            ele.forEach(e => elements.push(e));
          });
        });
      } else {
        const ele = document.getElementById('generate-questions-button');
        elements.push(ele);
      }

      elements.sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
      );

      if (elements.length > 0) {
        const errorElement = elements[0];
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' }); // scrollIntoView options are not supported in Safari
        errorElement.focus({ preventScroll: true });
        setCanFocus(false); // so the form doesn't suddenly jump to the next input that has error.
      }
    }
  }, [errors, canFocus]);

  const DottedLines = (
    <Box
      sx={{
        height: '54px',
        width: '0px',
        borderRight: theme =>
          `1px dashed ${theme.palette.border.primaryBlack2}`,
      }}
    />
  );

  return (
    <FormProvider
      methods={methods}
      onSubmit={async data => {
        handleAutoPilotWorkow(data);
      }}
    >
      <Stack width="80%" alignItems="center">
        <PreScreening
          watch={watch}
          errors={errors}
          setValue={setValue}
          isPreScreeningExpanded={isPreScreeningExpanded}
          setIsPreScreeningExpanded={setIsPreScreeningExpanded}
          preScreeningQuestionFields={preScreeningQuestionFields}
          appendPreScreeningQuestion={appendPreScreeningQuestion}
          removePreScreeningQuestion={removePreScreeningQuestion}
          savePreScreeningQuestions={savePreScreeningQuestions}
        />

        {DottedLines}

        <Screening
          watch={watch}
          errors={errors}
          jobSkills={jobSkills}
          isScreeningExpanded={isScreeningExpanded}
          setIsScreeningExpanded={setIsScreeningExpanded}
          preferredQualifyingCriteriaFields={preferredQualifyingCriteriaFields}
          appendPreferredQualifyingCriteria={appendPreferredQualifyingCriteria}
          removePreferredQualifyingCriteria={removePreferredQualifyingCriteria}
          savePreferredQualifyingCriterias={savePreferredQualifyingCriterias}
        />

        {DottedLines}

        <AIInterview
          watch={watch}
          errors={errors}
          methods={methods}
          setError={setError}
          clearErrors={clearErrors}
          setValue={setValue}
          getValues={getValues}
          isAiInterviewExpanded={isAiInterviewExpanded}
          setIsAiInterviewExpanded={setIsAiInterviewExpanded}
          interviewQuestionFields={interviewQuestionFields}
          appendInterviewQuestion={appendInterviewQuestion}
          removeInterviewQuestion={removeInterviewQuestion}
          saveInterviewQuestions={saveInterviewQuestions}
          saveAiInterviewDeadline={saveAiInterviewDeadline}
          codingAssessmentFields={codingAssessmentFields}
          appendCodingAssessment={appendCodingAssessment}
          removeCodingAssessment={removeCodingAssessment}
        />

        <Stack direction="row" alignItems="center" mt="54px" width="100%">
          <LoadingButton
            fullWidth
            loading={isSubmitting}
            loadingPosition="start"
            variant={
              mode === 'EDIT' ||
              (clickedStage === 'CONTINUE' && mode !== 'EDIT')
                ? 'contained'
                : 'soft'
            }
            sx={{
              color: theme => {
                if (
                  mode === 'EDIT' ||
                  (clickedStage === 'CONTINUE' && mode !== 'EDIT')
                ) {
                  return theme.palette.content.primaryWhite;
                }
                return theme.palette.content.primaryBlack1;
              },
              bgcolor: theme => {
                if (
                  mode === 'EDIT' ||
                  (clickedStage === 'CONTINUE' && mode !== 'EDIT')
                ) {
                  return theme.palette.background.primaryBlack1;
                }
                return theme.palette.background.primaryAccent2;
              },
            }}
            type="submit"
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
